import React from 'react'
import cx from 'classnames'

import { svgIconPropTypeObject } from '../prop-types/svg-icon-prop-type'

import styles from './svg-icon.module.css'

const propTypes = svgIconPropTypeObject

const defaultProps = {
    fillParent: false,
    flipHorizontal: false,
    flipVertical: false,
    inheritColor: false,
    keepPathColor: false,
    namespace: false,
    rotateDegrees: 0,
    viewBoxSize: 24
}

export const DEFAULT_SIZE = 18

/**
 * This is a wrapper component for inline SVG. It provides common and convenient functionality to reduce the effort for
 * creating icon components. It allows
 * - to set a size through properties,
 * - to set a CSS class (so one can set <path> fill colors) via `className` property later,
 * - to use differently sized SVGs (by using the viewBoxSize setting),
 * - and more...
 *
 * The generated SVG element follows the SVG 1.1 specification ({@link https://www.w3.org/TR/SVG11/}).
 *
 * Please stick to following icon rules, in order that an icon component
 * - is based on a UX design specification,
 * - has a name that describes the icon content, but not its purpose,
 * - has a kebab-case source file name that matches the exported component PascalCase name,
 * - has to return pure genuine SVG code based on the common `SvgIcon` component without any other wrapping elements,
 * - must be stateless,
 * - does not collect different visualization variants based on props,
 * - must not be colored by default and no accompanying CSS file shall be necessary,
 * - has a default view box size of 24 in the square (exceptions defined by UX spec),
 * - shall reduce the usage of grouping tags to a minimum and completely avoid groups if they do not provide any
 *   additional benefit,
 * - shall reduce the SVG drawing instructions into one path or shape, except the icon cannot be correctly drawn with
 *   just one path or shape,
 * - having groups and multiple paths or shapes should provide an id or class attribute for easy CSS selectors when
 *   colors shall be applied later.
 *
 * When creating a new SVG icon component make sure that
 * 1. you pass the correct viewBoxSize setting (found in `<svg viewBox="0 0 48 48">...</svg>`, 48 in this example)
 *    OR you set the viewBox (which then has priority) in case you need an origin that is not "0 0" (e.g. "0 5 48 48"),
 * 2. you copy/paste all the children you have within the SVG (as array, if multiple),
 * 3. you render `<SvgIcon {...props}>`.
 *
 * @example
 *
 * // my-icon.js
 * const MyIcon = (props) => (
 *     <SvgIcon viewBoxSize={24} >
 *         <path d="M4,4 L20,20 Z M20,4 L4,20" />
 *     </SvgIcon>
 * )
 *
 * // renders a 18x18 icon:
 * <MyIcon />
 *
 * // renders a 40x40 icon:
 * <MyIcon width={40} />
 *
 * // renders a 60x60 icon in red color:
 * <div style={{color: '#f00'}}><MyIcon width={60} inheritColor /></div>
 *
 * // renders a 60x60 white icon:
 * <MyIcon width={60} keepPathColor />
 *
 * // renders a 18x18 icon with colors specified for different paths:
 * <MyIcon className="colorize" />
 *
 * // my-icon.css
 * .colorize path { fill: #f00 }
 *
 * @param {SvgIconProps} [props] - SvgIcon element properties.
 * @returns {React.JSX.Element}
 */
const SvgIcon = props => {
    const {
        children,
        className,
        fillParent,
        flipHorizontal,
        flipVertical,
        height,
        inheritColor,
        keepPathColor,
        namespace,
        onBlur,
        onClick,
        onFocus,
        onMouseDown,
        onMouseEnter,
        onMouseLeave,
        onTouchStart,
        rotateDegrees,
        style,
        viewBox,
        viewBoxSize,
        width
    } = props

    const viewBoxSetting = viewBox || `0 0 ${viewBoxSize} ${viewBoxSize}`
    const background = `new ${viewBoxSetting}`
    const combinedClass = cx(
        styles['svg'],
        {
            [styles['inherit-color']]: inheritColor,
            [styles['default-color']]: !keepPathColor
        },
        className
    )

    const transformStylesArray = []
    if (flipHorizontal) {
        transformStylesArray.push('scaleX(-1)')
    }
    if (flipVertical) {
        transformStylesArray.push('scaleY(-1)')
    }
    if (rotateDegrees !== 0) {
        transformStylesArray.push(`rotate(${rotateDegrees}deg)`)
    }
    const transformStyle = transformStylesArray.length > 0 ? { transform: transformStylesArray.join(' ') } : {}
    const combinedStyle = Object.assign({}, transformStyle, style)
    const maxDimension = fillParent ? '100%' : null
    const namespaceData = namespace ? { xmlns: 'http://www.w3.org/2000/svg' } : {}
    const svgProps = {
        ...namespaceData,
        className: combinedClass,
        enableBackground: background,
        height: maxDimension || height || width || DEFAULT_SIZE,
        onBlur,
        onClick,
        onFocus,
        onMouseDown,
        onMouseEnter,
        onMouseLeave,
        onTouchStart,
        style: combinedStyle,
        version: '1.1',
        viewBox: viewBoxSetting,
        width: maxDimension || width || height || DEFAULT_SIZE,
        x: '0px',
        y: '0px'
    }

    return <svg {...svgProps}>{children}</svg>
}

SvgIcon.propTypes = propTypes
SvgIcon.defaultProps = defaultProps

export { SvgIcon }
